import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { mapApiErrorToUiModel, resolveUiErrorCopy } from '@formosa/shared/i18n/mapApiErrorToUiModel';
import { resolveSafeUserVisibleError } from '@formosa/shared/i18n/safeUserVisibleError';
import { DEFAULT_LOCALE, translate } from '@formosa/shared/i18n/translate';
import { formatDateTime } from '@formosa/shared/utils/formatDateTime';
import { journeyApi } from '../../api/journeyApi';
import { journeyDecisionApi } from '../../api/journeyDecisionApi';
import { useAuth } from '../../auth/AuthContext';
import { ErrorState, LoadingState } from '../../components/ui';
import {
  DECISIONS_PRODUCT_CHROME,
  decisionStatusProductLabel,
  fillWorkspaceProductChrome,
  WORKSPACE_PRODUCT_CHROME,
} from '../journey-workspace/journeyWorkspacePresentation';
import {
  classifyJourneyAccessError,
  normalizeJourneyWorkspace,
} from '../journey-workspace/journeyWorkspaceModel';
import JourneyWorkspaceNav from '../journey-workspace/JourneyWorkspaceNav';
import {
  DECISION_TYPES,
  ELIGIBILITY_RULES,
  buildCancelPayload,
  buildClosePayload,
  buildOpenPayload,
  buildUpdateDraftPayload,
  buildVotePayload,
  canManageDecision,
  classifyDecisionError,
  decisionTypeLabel,
  draftFromDecision,
  isDeadlinePassed,
  normalizeDecisionResults,
  normalizeJourneyDecision,
  ownBallotSummary,
  validateDecisionDraft,
} from './journeyDecisionModel';
import '../journey-workspace/journey-workspace.css';
import '../journey-workspace/journey-logistics.css';
import './journey-decision.css';

function statusClass(status) {
  if (status === 'OPEN') return 'is-open';
  if (status === 'DRAFT') return 'is-draft';
  if (status === 'CLOSED') return 'is-closed';
  if (status === 'CANCELLED') return 'is-cancelled';
  return '';
}

function mutationMessage(kind, fallback) {
  if (kind === 'conflict') return '這個決定剛剛有新的變更。';
  if (kind === 'not-eligible') return '你目前無法參與此投票。';
  if (kind === 'vote-change-forbidden') return DECISIONS_PRODUCT_CHROME.voteChangeForbiddenHint;
  if (kind === 'voting-closed') return '投票已結束。';
  if (kind === 'forbidden') return '你沒有權限執行此操作。';
  if (kind === 'lifecycle') return '目前旅程狀態不允許此操作。';
  if (kind === 'validation') return '請檢查輸入內容。';
  if (kind === 'not-found') return '決定不存在。';
  if (kind === 'unauthorized') return DECISIONS_PRODUCT_CHROME.sessionExpired;
  return fallback;
}

export default function JourneyDecisionDetailPage() {
  const { journeyId, decisionId } = useParams();
  const { user } = useAuth();
  const t = useCallback((key) => translate(DEFAULT_LOCALE, key), []);
  const viewerMemberId = user?.memberId ?? user?.id;
  const generation = useRef(0);
  const mutating = useRef(false);

  const [phase, setPhase] = useState('loading');
  const [errorModel, setErrorModel] = useState(null);
  const [journey, setJourney] = useState(null);
  const [decision, setDecision] = useState(null);
  const [results, setResults] = useState(null);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(null);
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState('');
  const [voteChoice, setVoteChoice] = useState(''); // optionId | '__abstain__'
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmClose, setConfirmClose] = useState(false);
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [needsReload, setNeedsReload] = useState(false);

  const titleId = useId();
  const descriptionId = useId();
  const typeId = useId();
  const eligibilityId = useId();
  const deadlineId = useId();
  const minPartId = useId();
  const cancelReasonId = useId();

  const load = useCallback(async ({ soft = false } = {}) => {
    const requestId = ++generation.current;
    const scopedJourneyId = String(journeyId || '');
    const scopedDecisionId = String(decisionId || '');
    if (!soft) {
      setPhase('loading');
      setErrorModel(null);
      setEditing(false);
      setDraft(null);
      setConfirmOpen(false);
      setConfirmClose(false);
      setConfirmCancel(false);
      setFormError('');
      setNeedsReload(false);
    }
    try {
      const [detailPayload, decisionPayload] = await Promise.all([
        journeyApi.getDetail(scopedJourneyId),
        journeyDecisionApi.get(scopedJourneyId, scopedDecisionId),
      ]);
      if (requestId !== generation.current) return;
      if (String(journeyId) !== scopedJourneyId || String(decisionId) !== scopedDecisionId) return;

      const nextJourney = normalizeJourneyWorkspace(detailPayload, viewerMemberId);
      const nextDecision = normalizeJourneyDecision(decisionPayload);
      setJourney(nextJourney);
      setDecision(nextDecision);
      setVoteChoice(
        nextDecision.actorBallot?.abstained
          ? '__abstain__'
          : (nextDecision.actorBallot?.optionId || ''),
      );

      let nextResults = null;
      if (nextDecision.isOpen || nextDecision.isClosed) {
        try {
          const resultsPayload = await journeyDecisionApi.getResults(scopedJourneyId, scopedDecisionId);
          if (requestId !== generation.current) return;
          if (String(journeyId) !== scopedJourneyId || String(decisionId) !== scopedDecisionId) return;
          nextResults = normalizeDecisionResults(resultsPayload);
        } catch (cause) {
          if (requestId !== generation.current) return;
          const kind = classifyDecisionError(cause);
          if (kind !== 'not-found') throw cause;
        }
      }
      setResults(nextResults);
      setPhase('ready');
    } catch (cause) {
      if (requestId !== generation.current) return;
      const kind = classifyDecisionError(cause);
      const access = classifyJourneyAccessError(cause);
      // 決定詳情頁：decision.not.found 的 key 也含 not.found，不可先被行程找不到吃掉。
      if (kind === 'journey-not-found') setPhase('not-found');
      else if (kind === 'not-found') setPhase('decision-not-found');
      else if (access === 'not-found') setPhase('decision-not-found');
      else if (kind === 'forbidden' || access === 'forbidden') setPhase('forbidden');
      else {
        setErrorModel(mapApiErrorToUiModel(cause, { t }));
        setPhase('error');
      }
    }
  }, [decisionId, journeyId, t, viewerMemberId]);

  useEffect(() => {
    if (!/^[1-9]\d*$/.test(journeyId || '')) {
      setPhase('not-found');
      return undefined;
    }
    if (!/^[1-9]\d*$/.test(decisionId || '')) {
      setPhase('decision-not-found');
      return undefined;
    }
    load();
    return () => { generation.current += 1; };
  }, [journeyId, decisionId, load]);

  async function runMutation(work, { successSoft = true } = {}) {
    if (mutating.current) return;
    const requestId = generation.current;
    mutating.current = true;
    setBusy(true);
    setFormError('');
    try {
      await work();
      if (requestId !== generation.current) return;
      setConfirmOpen(false);
      setConfirmClose(false);
      setConfirmCancel(false);
      setEditing(false);
      setDraft(null);
      if (successSoft) await load({ soft: true });
    } catch (cause) {
      if (requestId !== generation.current) return;
      const kind = classifyDecisionError(cause);
      setFormError(mutationMessage(
        kind,
        resolveSafeUserVisibleError(cause, t, 'travel.common.actionFailed') || '操作失敗。',
      ));
      setNeedsReload(kind === 'conflict');
    } finally {
      mutating.current = false;
      setBusy(false);
    }
  }

  function startEdit() {
    setDraft(draftFromDecision(decision));
    setEditing(true);
    setFormError('');
  }

  function updateOption(index, patch) {
    setDraft((current) => ({
      ...current,
      options: current.options.map((row, i) => (i === index ? { ...row, ...patch } : row)),
    }));
  }

  function addOption() {
    setDraft((current) => ({
      ...current,
      options: [
        ...current.options,
        { optionId: null, label: '', description: '', displayOrder: current.options.length + 1 },
      ],
    }));
  }

  function removeOption(index) {
    setDraft((current) => {
      if (current.options.length <= 2) return current;
      return {
        ...current,
        options: current.options
          .filter((_, i) => i !== index)
          .map((row, i) => ({ ...row, displayOrder: i + 1 })),
      };
    });
  }

  async function saveDraft(event) {
    event.preventDefault();
    const validation = validateDecisionDraft(draft);
    if (validation) {
      setFormError(validation);
      return;
    }
    await runMutation(async () => {
      await journeyDecisionApi.updateDraft(
        journey.journeyId,
        decision.decisionId,
        buildUpdateDraftPayload(draft, { expectedDecisionRowVersion: decision.rowVersion }),
      );
    });
  }

  async function openDecision() {
    await runMutation(async () => {
      await journeyDecisionApi.open(
        journey.journeyId,
        decision.decisionId,
        buildOpenPayload({ expectedDecisionRowVersion: decision.rowVersion }),
      );
    });
  }

  async function submitVote(event) {
    event.preventDefault();
    if (!voteChoice) {
      setFormError('請選擇一個選項，或棄權。');
      return;
    }
    const abstained = voteChoice === '__abstain__';
    await runMutation(async () => {
      await journeyDecisionApi.vote(
        journey.journeyId,
        decision.decisionId,
        buildVotePayload({
          expectedDecisionRowVersion: decision.rowVersion,
          optionId: abstained ? null : voteChoice,
          abstained,
        }),
      );
    });
  }

  async function closeDecision() {
    await runMutation(async () => {
      await journeyDecisionApi.close(
        journey.journeyId,
        decision.decisionId,
        buildClosePayload({ expectedDecisionRowVersion: decision.rowVersion }),
      );
    });
  }

  async function cancelDecision() {
    const reason = String(cancelReason || '').trim();
    if (!reason) {
      setFormError('取消原因為必填。');
      return;
    }
    await runMutation(async () => {
      await journeyDecisionApi.cancel(
        journey.journeyId,
        decision.decisionId,
        buildCancelPayload({
          expectedDecisionRowVersion: decision.rowVersion,
          reason,
        }),
      );
    });
  }

  if (phase === 'loading') {
    return (
      <main className="journey-workspace-state">
        <LoadingState label={t('travel.common.loading')} data-testid="decision-detail-loading" />
      </main>
    );
  }
  if (phase === 'not-found') {
    return (
      <main className="journey-workspace-state">
        <h1>{WORKSPACE_PRODUCT_CHROME.notFoundTitle}</h1>
        <Link to="/journeys">{WORKSPACE_PRODUCT_CHROME.backToMyJourneys}</Link>
      </main>
    );
  }
  if (phase === 'decision-not-found') {
    return (
      <main className="journey-workspace-state">
        <h1>{DECISIONS_PRODUCT_CHROME.decisionNotFoundTitle}</h1>
        <p>{DECISIONS_PRODUCT_CHROME.decisionNotFoundBody}</p>
        <Link to={`/journeys/${journeyId}/decisions`}>{DECISIONS_PRODUCT_CHROME.backToDecisions}</Link>
      </main>
    );
  }
  if (phase === 'forbidden') {
    return (
      <main className="journey-workspace-state">
        <h1>{WORKSPACE_PRODUCT_CHROME.forbiddenTitle}</h1>
        <Link to="/journeys">{WORKSPACE_PRODUCT_CHROME.backToMyJourneys}</Link>
      </main>
    );
  }
  if (phase === 'error') {
    const errorCopy = resolveUiErrorCopy(errorModel, t);
    return (
      <main className="journey-workspace-state" data-testid="decision-detail-error">
        <ErrorState
          title={DECISIONS_PRODUCT_CHROME.openDetailFailedTitle}
          description={errorCopy.description}
          retryable
          retryLabel={DECISIONS_PRODUCT_CHROME.tryAgain}
          onRetry={load}
        />
      </main>
    );
  }

  const canManage = canManageDecision({
    role: journey?.requesterRole,
    lifecycleStatus: journey?.lifecycleStatus,
    createdByMemberId: decision?.createdByMemberId,
    viewerMemberId,
  });
  const deadlinePassed = isDeadlinePassed(decision?.deadlineAt);
  const hasVoted = Boolean(decision?.actorBallot);
  const canVote = decision?.isOpen
    && decision?.actorEligible
    && (!hasVoted || decision?.allowVoteChange)
    && !deadlinePassed;
  const selectedOption = (decision?.options || []).find(
    (row) => String(row.optionId) === String(decision?.selectedOptionId),
  );
  const resultCounts = results?.optionVoteCounts?.length
    ? results.optionVoteCounts
    : (decision?.optionVoteCounts || []);

  return (
    <main className="journey-workspace">
      <header className="journey-workspace__header">
        <div>
          <p className="journey-workspace__eyebrow">{WORKSPACE_PRODUCT_CHROME.eyebrow}</p>
          <h1>{journey?.title || DECISIONS_PRODUCT_CHROME.untitledJourney}</h1>
          <p className="journey-workspace__meta">
            {journey?.lifecycleStatusLabel}
            {journey?.requesterRoleLabel ? ` · ${journey.requesterRoleLabel}` : ''}
          </p>
        </div>
        <Link className="journey-workspace__back" to={`/journeys/${journey.journeyId}/decisions`}>
          {DECISIONS_PRODUCT_CHROME.backToDecisions}
        </Link>
      </header>
      <JourneyWorkspaceNav journeyId={journey.journeyId} />

      <section className="journey-workspace__panel journey-decision" aria-labelledby="decision-detail-heading">
        <div className="journey-workspace__panel-head">
          <h2 id="decision-detail-heading">{decision.title}</h2>
        </div>

        <p className="journey-decision__meta">
          <span className={`journey-decision__status ${statusClass(decision.decisionStatus)}`}>
            {decisionStatusProductLabel(decision.decisionStatus)}
          </span>
          <span>{decision.decisionTypeLabel}</span>
          {decision.createTime ? (
            <time dateTime={decision.createTime}>{formatDateTime(decision.createTime)}</time>
          ) : null}
          {decision.deadlineAt ? (
            <span>{DECISIONS_PRODUCT_CHROME.deadlinePrefix} {formatDateTime(decision.deadlineAt)}</span>
          ) : null}
        </p>

        {decision.description ? <p>{decision.description}</p> : null}

        {formError ? (
          <p className="journey-decision__error" role="alert">
            {formError}
            {needsReload ? (
              <>
                {' '}
                <button type="button" disabled={busy} onClick={() => load()}>重新載入</button>
              </>
            ) : null}
          </p>
        ) : null}

        {decision.isDraft && editing && draft ? (
          <form className="journey-decision__form" onSubmit={saveDraft} noValidate>
            <label htmlFor={titleId}>
              問題 / 標題
              <input
                id={titleId}
                value={draft.title}
                maxLength={100}
                onChange={(e) => setDraft((c) => ({ ...c, title: e.target.value }))}
              />
            </label>
            <label htmlFor={descriptionId}>
              說明（選填）
              <textarea
                id={descriptionId}
                rows={3}
                maxLength={1000}
                value={draft.description}
                onChange={(e) => setDraft((c) => ({ ...c, description: e.target.value }))}
              />
            </label>
            <div className="journey-decision__grid-2">
              <label htmlFor={typeId}>
                類型
                <select
                  id={typeId}
                  value={draft.decisionType}
                  onChange={(e) => setDraft((c) => ({ ...c, decisionType: e.target.value }))}
                >
                  {DECISION_TYPES.map((type) => (
                    <option key={type} value={type}>{decisionTypeLabel(type)}</option>
                  ))}
                </select>
              </label>
              <label htmlFor={eligibilityId}>
                誰可以投票
                <select
                  id={eligibilityId}
                  value={draft.eligibilityRule}
                  onChange={(e) => setDraft((c) => ({ ...c, eligibilityRule: e.target.value }))}
                >
                  {ELIGIBILITY_RULES.map((rule) => (
                    <option key={rule} value={rule}>
                      {rule === 'ACTIVE_JOURNEY_MEMBERS'
                        ? DECISIONS_PRODUCT_CHROME.eligibilityAllMembers
                        : DECISIONS_PRODUCT_CHROME.eligibilityOwnersOnly}
                    </option>
                  ))}
                </select>
              </label>
            </div>
            <div className="journey-decision__grid-2">
              <label htmlFor={deadlineId}>
                {DECISIONS_PRODUCT_CHROME.deadlineOptional}
                <input
                  id={deadlineId}
                  type="datetime-local"
                  value={draft.deadlineAt}
                  onChange={(e) => setDraft((c) => ({ ...c, deadlineAt: e.target.value }))}
                />
              </label>
              <label htmlFor={minPartId}>
                最低參與人數（選填）
                <input
                  id={minPartId}
                  inputMode="numeric"
                  value={draft.minimumParticipationCount}
                  onChange={(e) => setDraft((c) => ({
                    ...c,
                    minimumParticipationCount: e.target.value,
                  }))}
                />
              </label>
            </div>
            <div className="journey-decision__checks">
              <label>
                <input
                  type="checkbox"
                  checked={draft.allowAbstain}
                  onChange={(e) => setDraft((c) => ({ ...c, allowAbstain: e.target.checked }))}
                />
                允許棄權
              </label>
              <label>
                <input
                  type="checkbox"
                  checked={draft.allowVoteChange}
                  onChange={(e) => setDraft((c) => ({ ...c, allowVoteChange: e.target.checked }))}
                />
                開放投票後可改票
              </label>
            </div>
            <fieldset className="journey-decision__options">
              <legend>選項（至少 2 個）</legend>
              {draft.options.map((row, index) => (
                <div className="journey-decision__option-row" key={row.optionId || `new-${index}`}>
                  <label>
                    選項 {index + 1}
                    <input
                      value={row.label}
                      maxLength={100}
                      onChange={(e) => updateOption(index, { label: e.target.value })}
                    />
                  </label>
                  <label>
                    說明（選填）
                    <input
                      value={row.description}
                      maxLength={500}
                      onChange={(e) => updateOption(index, { description: e.target.value })}
                    />
                  </label>
                  <div className="journey-decision__option-actions">
                    <button
                      type="button"
                      disabled={draft.options.length <= 2 || busy}
                      onClick={() => removeOption(index)}
                    >
                      移除
                    </button>
                  </div>
                </div>
              ))}
              <button type="button" disabled={busy} onClick={addOption}>新增選項</button>
            </fieldset>
            <div className="journey-decision__actions">
              <button type="submit" disabled={busy}>
                {busy ? WORKSPACE_PRODUCT_CHROME.saving : DECISIONS_PRODUCT_CHROME.saveDraft}
              </button>
              <button type="button" disabled={busy} onClick={() => { setEditing(false); setDraft(null); }}>
                {DECISIONS_PRODUCT_CHROME.cancelEdit}
              </button>
            </div>
          </form>
        ) : (
          <>
            <h3>{DECISIONS_PRODUCT_CHROME.optionsHeading}</h3>
            <ul className="journey-decision__list">
              {(decision.options || []).map((option) => (
                <li key={option.optionId} className="journey-decision__card">
                  <p className="journey-decision__card-title">{option.label}</p>
                  {option.description ? <p>{option.description}</p> : null}
                </li>
              ))}
            </ul>
          </>
        )}

        {decision.isDraft && canManage && !editing ? (
          <div className="journey-decision__actions">
            <button type="button" disabled={busy} onClick={startEdit}>
              {DECISIONS_PRODUCT_CHROME.editDraft}
            </button>
            <button type="button" disabled={busy} onClick={() => setConfirmOpen(true)}>
              {DECISIONS_PRODUCT_CHROME.openForVoting}
            </button>
            <button type="button" disabled={busy} onClick={() => setConfirmCancel(true)}>
              {DECISIONS_PRODUCT_CHROME.cancelDecision}
            </button>
          </div>
        ) : null}

        {confirmOpen ? (
          <div className="journey-decision__confirm" role="dialog" aria-modal="true" aria-labelledby="open-confirm-title">
            <h3 id="open-confirm-title">{DECISIONS_PRODUCT_CHROME.openForVotingConfirmTitle}</h3>
            <p>{DECISIONS_PRODUCT_CHROME.openForVotingConfirmBody}</p>
            <div className="journey-decision__actions">
              <button type="button" disabled={busy} onClick={openDecision}>
                {busy ? DECISIONS_PRODUCT_CHROME.opening : DECISIONS_PRODUCT_CHROME.confirmOpen}
              </button>
              <button type="button" disabled={busy} onClick={() => setConfirmOpen(false)}>
                {DECISIONS_PRODUCT_CHROME.dialogBack}
              </button>
            </div>
          </div>
        ) : null}

        {decision.isOpen ? (
          <section aria-labelledby="vote-heading">
            <h3 id="vote-heading">{DECISIONS_PRODUCT_CHROME.votingHeading}</h3>
            <p className="journey-decision__ballot">{ownBallotSummary(decision)}</p>
            {deadlinePassed ? (
              <p className="journey-decision__deadline-passed" role="status">
                {DECISIONS_PRODUCT_CHROME.deadlinePassed}
              </p>
            ) : null}
            {!decision.actorEligible ? (
              <p className="journey-decision__hint" role="status">你目前無法參與此投票。</p>
            ) : null}
            {hasVoted && !decision.allowVoteChange ? (
              <p className="journey-decision__hint" role="status">
                {DECISIONS_PRODUCT_CHROME.voteChangeForbiddenHint}
              </p>
            ) : null}

            {canVote ? (
              <form className="journey-decision__vote" onSubmit={submitVote}>
                <fieldset className="journey-decision__vote-options">
                  <legend>選擇一個選項</legend>
                  {(decision.options || []).map((option) => (
                    <label className="journey-decision__vote-option" key={option.optionId}>
                      <input
                        type="radio"
                        name="decision-vote"
                        value={option.optionId}
                        checked={voteChoice === String(option.optionId)}
                        onChange={() => setVoteChoice(String(option.optionId))}
                      />
                      <span>
                        <strong>{option.label}</strong>
                        {option.description ? <> — {option.description}</> : null}
                      </span>
                    </label>
                  ))}
                  {decision.allowAbstain ? (
                    <label className="journey-decision__vote-option">
                      <input
                        type="radio"
                        name="decision-vote"
                        value="__abstain__"
                        checked={voteChoice === '__abstain__'}
                        onChange={() => setVoteChoice('__abstain__')}
                      />
                      <span><strong>棄權</strong></span>
                    </label>
                  ) : null}
                </fieldset>
                <button type="submit" disabled={busy}>
                  {busy
                    ? DECISIONS_PRODUCT_CHROME.submitting
                    : (hasVoted
                      ? DECISIONS_PRODUCT_CHROME.changeVote
                      : DECISIONS_PRODUCT_CHROME.submitVote)}
                </button>
              </form>
            ) : null}

            {canManage ? (
              <div className="journey-decision__actions">
                <button type="button" disabled={busy} onClick={() => setConfirmClose(true)}>
                  {DECISIONS_PRODUCT_CHROME.closeDecision}
                </button>
                <button type="button" disabled={busy} onClick={() => setConfirmCancel(true)}>
                  {DECISIONS_PRODUCT_CHROME.cancelDecision}
                </button>
              </div>
            ) : null}
          </section>
        ) : null}

        {confirmClose ? (
          <div className="journey-decision__confirm" role="dialog" aria-modal="true" aria-labelledby="close-confirm-title">
            <h3 id="close-confirm-title">{DECISIONS_PRODUCT_CHROME.closeConfirmTitle}</h3>
            <p>{DECISIONS_PRODUCT_CHROME.closeConfirmBody}</p>
            <div className="journey-decision__actions">
              <button type="button" disabled={busy} onClick={closeDecision}>
                {busy ? DECISIONS_PRODUCT_CHROME.closing : DECISIONS_PRODUCT_CHROME.confirmClose}
              </button>
              <button type="button" disabled={busy} onClick={() => setConfirmClose(false)}>
                {DECISIONS_PRODUCT_CHROME.dialogBack}
              </button>
            </div>
          </div>
        ) : null}

        {confirmCancel ? (
          <div className="journey-decision__confirm" role="dialog" aria-modal="true" aria-labelledby="cancel-confirm-title">
            <h3 id="cancel-confirm-title">{DECISIONS_PRODUCT_CHROME.cancelDecisionConfirmTitle}</h3>
            <label htmlFor={cancelReasonId}>
              取消原因
              <textarea
                id={cancelReasonId}
                rows={3}
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
              />
            </label>
            <div className="journey-decision__actions">
              <button type="button" disabled={busy} onClick={cancelDecision}>
                {busy ? DECISIONS_PRODUCT_CHROME.cancelling : DECISIONS_PRODUCT_CHROME.confirmCancel}
              </button>
              <button type="button" disabled={busy} onClick={() => setConfirmCancel(false)}>
                {DECISIONS_PRODUCT_CHROME.dialogBack}
              </button>
            </div>
          </div>
        ) : null}

        {(decision.isOpen || decision.isClosed) && (results || resultCounts.length > 0) ? (
          <section aria-labelledby="results-heading">
            <h3 id="results-heading">{DECISIONS_PRODUCT_CHROME.resultsHeading}</h3>
            <dl className="journey-decision__results">
              <div>
                <dt>{DECISIONS_PRODUCT_CHROME.eligible}</dt>
                <dd>{results?.eligibleCount ?? decision.eligibleCount}</dd>
              </div>
              <div>
                <dt>{DECISIONS_PRODUCT_CHROME.participated}</dt>
                <dd>{results?.participationCount ?? decision.participationCount}</dd>
              </div>
              <div>
                <dt>{DECISIONS_PRODUCT_CHROME.abstain}</dt>
                <dd>{results?.abstainCount ?? decision.abstainCount}</dd>
              </div>
              {(results?.tie ?? decision.tie) ? (
                <div>
                  <dt>{DECISIONS_PRODUCT_CHROME.outcome}</dt>
                  <dd>{DECISIONS_PRODUCT_CHROME.outcomeTie}</dd>
                </div>
              ) : null}
              {(results?.participationThresholdMet ?? decision.participationThresholdMet) != null ? (
                <div>
                  <dt>{DECISIONS_PRODUCT_CHROME.participationThreshold}</dt>
                  <dd>
                    {(results?.participationThresholdMet ?? decision.participationThresholdMet)
                      ? DECISIONS_PRODUCT_CHROME.thresholdMet
                      : DECISIONS_PRODUCT_CHROME.thresholdNotMet}
                  </dd>
                </div>
              ) : null}
            </dl>
            <ul className="journey-decision__results">
              {(decision.options || []).map((option) => {
                const countRow = resultCounts.find(
                  (row) => String(row.optionId) === String(option.optionId),
                );
                return (
                  <li key={option.optionId}>
                    <strong>{option.label}</strong>
                    <span>
                      {fillWorkspaceProductChrome(DECISIONS_PRODUCT_CHROME.voteCount, {
                        count: countRow ? countRow.voteCount : 0,
                      })}
                    </span>
                  </li>
                );
              })}
            </ul>
          </section>
        ) : null}

        {decision.isClosed ? (
          <div className="journey-decision__resolution" role="status">
            <h3>{DECISIONS_PRODUCT_CHROME.resolutionHeading}</h3>
            {decision.tie ? (
              <p>平票，沒有單一勝者。</p>
            ) : selectedOption ? (
              <p>選定：{selectedOption.label}</p>
            ) : (
              <p>已結案。</p>
            )}
            <p className="journey-decision__hint">
              {DECISIONS_PRODUCT_CHROME.closedHint}
            </p>
          </div>
        ) : null}

        {decision.isCancelled ? (
          <div className="journey-decision__resolution" role="status">
            <h3>{DECISIONS_PRODUCT_CHROME.cancelledHeading}</h3>
            {decision.cancellationReason ? <p>{decision.cancellationReason}</p> : null}
          </div>
        ) : null}
      </section>
    </main>
  );
}
